import { usePayerAutocomplete } from "@usebridge/sdk-react"
import React from "react"
import { Autocomplete, InputAdornment, SearchIcon, TextField } from "@carrotfertility/carotene-core"
import { useController } from "@carrotfertility/carotene-core-x"
import { useIntl } from "react-intl"
import type { CoverageDetails } from "./types"

export function InsuranceProviderField(): JSX.Element {
  const intl = useIntl()
  const [query, setQuery] = React.useState("")
  const { results, isLoading, error } = usePayerAutocomplete(query)
  const options = results.map((payer) => ({ value: payer.id, label: payer.name }))
  if (!query.trim()) {
    options.sort((a, b) => a.label.localeCompare(b.label, intl.locale, { sensitivity: "base" }))
  }
  const { field, fieldState, formState } = useController<CoverageDetails, "insuranceProvider">({
    name: "insuranceProvider",
    rules: { required: intl.formatMessage({ defaultMessage: "Required" }) }
  })

  return (
    <Autocomplete
      disablePortal
      fullWidth
      options={options}
      filterOptions={(options) => options}
      loading={isLoading}
      inputValue={query}
      onInputChange={(_, value, reason) => {
        // Clearing a previous selection also emits an empty reset; keep the new search text.
        if (reason === "reset" && !value) return
        setQuery(value)
        if (reason === "input" || reason === "clear") field.onChange(null)
      }}
      noOptionsText={intl.formatMessage({ defaultMessage: "No insurance providers found." })}
      value={field.value}
      onChange={(_, value) => field.onChange(value)}
      onBlur={field.onBlur}
      disabled={formState.isSubmitting}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      renderInput={(params) => (
        <TextField
          {...params}
          name={field.name}
          inputRef={field.ref}
          required
          label={intl.formatMessage({ defaultMessage: "Insurance provider" })}
          placeholder={intl.formatMessage({ defaultMessage: "Search your insurance company" })}
          error={Boolean(fieldState.error || error)}
          helperText={
            error
              ? intl.formatMessage({
                  defaultMessage: "Unable to load insurance providers. Please try searching again."
                })
              : fieldState.error?.message
          }
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <InputAdornment position="start">
                  <SearchIcon aria-hidden="true" />
                </InputAdornment>
                {params.InputProps.startAdornment}
              </>
            )
          }}
        />
      )}
    />
  )
}
